import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UIkit from "uikit";
import { workspace } from "@checktiv/sdk-web/workspace";

import Dialog from "../common/Dialog";
import IdentityVerificationService from "../../services/IdentityVerificationService";
import { normalizeStatus, statusMeta } from "./identityVerificationStatus";
import {
  clearDecisionReconciliation,
  DECISION_RECONCILIATION_DELAY_MS,
  DELAYED_STATUS_POLL_INTERVAL_MS,
  getDecisionReconciliationStatus,
  isDecisionReconciliationResolved,
  MAX_STATUS_REFRESH_FAILURES,
  readDecisionReconciliation,
  STATUS_POLL_INTERVAL_MS,
  statusRefreshDelay,
  writeDecisionReconciliation,
} from "./identityVerificationReconciliation";

const tokenBundle = ({ framingToken, dataToken, expiresAt }) => ({
  framingToken,
  dataToken,
  expiresAt,
});

const TERMINAL_STATUSES = new Set([
  "verified",
  "approved",
  "declined",
  "failed",
  "expired",
  "cancelled",
]);
const IdentityVerificationModal = ({
  open,
  reservation = {},
  status,
  onClose,
  onStatusChange,
}) => {
  const targetRef = useRef(null);
  const guestReservationId = reservation.guest_reservation_id;
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusRefreshError, setStatusRefreshError] = useState(null);
  const [decisionReconciliation, setDecisionReconciliation] = useState(() =>
    readDecisionReconciliation(guestReservationId),
  );
  const decisionReconciliationRef = useRef(decisionReconciliation);
  const decisionRevisionRef = useRef(0);
  const definitiveStatusRef = useRef(
    decisionReconciliation?.baselineStatus || normalizeStatus(status),
  );
  const modalId = useMemo(
    () => `identity-verification-review-${guestReservationId}`,
    [guestReservationId],
  );
  const normalizedStatus = normalizeStatus(status);
  const meta = statusMeta[normalizedStatus] || statusMeta.not_required;

  useEffect(() => {
    if (!["syncing", "sync_delayed"].includes(normalizedStatus)) {
      definitiveStatusRef.current = normalizedStatus;
    }
  }, [normalizedStatus]);

  const refreshStatus = useCallback(async () => {
    try {
      const result = await IdentityVerificationService.refreshStatus(guestReservationId);
      return {
        status: result?.verification_status ? normalizeStatus(result.verification_status) : null,
        failed: false,
        error: null,
      };
    } catch (refreshError) {
      console.error("Failed to refresh identity verification status", refreshError);
      return {
        status: null,
        failed: true,
        error: "Status updates are temporarily unavailable. The verification review is still available.",
      };
    }
  }, [guestReservationId]);

  const startDecisionReconciliation = useCallback(() => {
    decisionRevisionRef.current += 1;
    const nextReconciliation = {
      baselineStatus: definitiveStatusRef.current,
      startedAt: Date.now(),
    };
    decisionReconciliationRef.current = nextReconciliation;
    writeDecisionReconciliation(guestReservationId, nextReconciliation);
    setDecisionReconciliation(nextReconciliation);
    onStatusChange?.("syncing");
  }, [guestReservationId, onStatusChange]);

  useEffect(() => {
    if (!open || !guestReservationId || decisionReconciliation) return undefined;

    let disposed = false;
    let refreshTimer = null;
    let consecutiveFailures = 0;

    const reconcileStatus = async () => {
      const revision = decisionRevisionRef.current;
      const result = await refreshStatus();
      // A pre-decision response must never replace Updating or a newer decision.
      if (disposed || revision !== decisionRevisionRef.current
        || decisionReconciliationRef.current) return;
      setStatusRefreshError(result.error);
      if (result.status) {
        definitiveStatusRef.current = result.status;
        onStatusChange?.(result.status);
      }
      if (TERMINAL_STATUSES.has(result.status)) return;
      consecutiveFailures = result.failed ? consecutiveFailures + 1 : 0;
      if (consecutiveFailures >= MAX_STATUS_REFRESH_FAILURES) return;
      refreshTimer = window.setTimeout(
        reconcileStatus,
        statusRefreshDelay(consecutiveFailures),
      );
    };

    reconcileStatus();
    return () => {
      disposed = true;
      if (refreshTimer) window.clearTimeout(refreshTimer);
    };
  }, [decisionReconciliation, guestReservationId, onStatusChange, open, refreshStatus]);

  useEffect(() => {
    if (!guestReservationId || !decisionReconciliation) return undefined;

    let disposed = false;
    let refreshTimer = null;
    let lastPublishedStatus = null;
    let consecutiveFailures = 0;

    const reconcileDecision = async () => {
      const result = await refreshStatus();
      if (disposed || decisionReconciliationRef.current !== decisionReconciliation) return;
      setStatusRefreshError(result.error);
      consecutiveFailures = result.failed ? consecutiveFailures + 1 : 0;
      const nextStatus = result.status;

      if (isDecisionReconciliationResolved(decisionReconciliation, nextStatus)) {
        definitiveStatusRef.current = nextStatus;
        onStatusChange?.(nextStatus);
        decisionReconciliationRef.current = null;
        clearDecisionReconciliation(guestReservationId);
        setDecisionReconciliation(null);
        return;
      }

      const reconciliationStatus = getDecisionReconciliationStatus(
        decisionReconciliation,
      );
      if (reconciliationStatus !== lastPublishedStatus) {
        lastPublishedStatus = reconciliationStatus;
        onStatusChange?.(reconciliationStatus);
      }

      refreshTimer = window.setTimeout(
        reconcileDecision,
        Math.max(
          statusRefreshDelay(consecutiveFailures),
          reconciliationStatus === "sync_delayed"
            ? DELAYED_STATUS_POLL_INTERVAL_MS
            : STATUS_POLL_INTERVAL_MS,
        ),
      );
    };

    reconcileDecision();
    return () => {
      disposed = true;
      if (refreshTimer) window.clearTimeout(refreshTimer);
    };
  }, [decisionReconciliation, guestReservationId, onStatusChange, refreshStatus]);

  useEffect(() => {
    const element = document.getElementById(modalId);
    if (!element) return undefined;

    const modal = UIkit.modal(element);
    let disposed = false;
    const handleHidden = async () => {
      if (decisionReconciliationRef.current) {
        onClose?.();
        return;
      }
      const revision = decisionRevisionRef.current;
      const result = await refreshStatus();
      if (disposed) return;
      if (revision === decisionRevisionRef.current && !decisionReconciliationRef.current) {
        setStatusRefreshError(result.error);
        if (result.status) {
          definitiveStatusRef.current = result.status;
          onStatusChange?.(result.status);
        }
      }
      onClose?.();
    };
    element.addEventListener("hidden", handleHidden);
    if (open) modal.show();
    else modal.hide();

    return () => {
      disposed = true;
      element.removeEventListener("hidden", handleHidden);
    };
  }, [modalId, onClose, onStatusChange, open, refreshStatus]);

  useEffect(() => {
    if (!open || !guestReservationId || !targetRef.current) return undefined;

    let disposed = false;
    let reviewerHandle = null;
    let initialToken = null;
    setLoading(true);
    setError(null);

    const mountReviewer = async () => {
      try {
        initialToken = await IdentityVerificationService.createWorkspaceToken(
          guestReservationId,
          "initial",
        );
        if (disposed) return;

        const reviewerSessionId = initialToken.sessionId;

        const client = workspace({
          region: "us",
          getToken: async ({ reason }) => {
            if (reason === "initial" && initialToken) {
              const first = tokenBundle(initialToken);
              initialToken = null;
              return first;
            }
            const next = await IdentityVerificationService.createWorkspaceToken(
              guestReservationId,
              reason,
            );
            return tokenBundle(next);
          },
          theme: {
            colorMode: "light",
            brandName: "RueBaRue",
          },
        });

        reviewerHandle = client.mount("reviewer", {
          sessionId: reviewerSessionId,
          target: targetRef.current,
          onEvent: (event) => {
            if (disposed) return;
            if (
              event?.type === "checktiv.workspace.navigate" &&
              event?.intent === "session_complete"
            ) {
              IdentityVerificationService.recordReviewDecisionSubmitted(
                guestReservationId,
                definitiveStatusRef.current,
              ).catch((auditError) => {
                console.error("Failed to record identity verification reviewer action", auditError);
              });
              startDecisionReconciliation();
            }
            if (event?.type === "checktiv.workspace.error") {
              setError("We couldn't load the verification review. Please try again.");
            }
          },
        });
        if (!disposed) setLoading(false);
      } catch (mountError) {
        if (!disposed) {
          console.error("Failed to load Checktiv Workspace reviewer", mountError);
          setError("We couldn't load the verification review. Please try again.");
          setLoading(false);
        }
      }
    };

    mountReviewer();
    return () => {
      disposed = true;
      reviewerHandle?.destroy?.();
    };
  }, [attempt, guestReservationId, open, startDecisionReconciliation]);

  return (
    <Dialog id={modalId} className="identity-verification-review-modal">
      <div className="uk-modal-dialog identity-verification-review-dialog">
        <button
          className="uk-modal-close-default"
          type="button"
          aria-label="Close"
          uk-close="true"
        ></button>
        <div className="uk-modal-header identity-verification-review-header">
          <h2 className="uk-modal-title">ID Verification</h2>
          <span className={`identity-verification-review-status identity-verification-review-status--${meta.tone}`}>
            {meta.label}
          </span>
        </div>
        <div className="uk-modal-body identity-verification-review-body">
          {loading && (
            <div className="identity-verification-review-message" aria-live="polite">
              <span uk-spinner="ratio: 0.8"></span>
              Loading verification review...
            </div>
          )}
          {error && (
            <div className="identity-verification-review-message identity-verification-review-error">
              <span>{error}</span>
              <button type="button" className="uk-button uk-button-text" onClick={() => setAttempt((value) => value + 1)}>
                Try again
              </button>
            </div>
          )}
          {statusRefreshError && !error && (
            <div
              className="identity-verification-review-warning"
              role="status"
            >
              {statusRefreshError}
            </div>
          )}
          <div
            ref={targetRef}
            className="identity-verification-review-target"
            aria-hidden={loading || Boolean(error)}
          ></div>
        </div>
      </div>
    </Dialog>
  );
};

export {
  DECISION_RECONCILIATION_DELAY_MS,
  DELAYED_STATUS_POLL_INTERVAL_MS,
  MAX_STATUS_REFRESH_FAILURES,
  STATUS_POLL_INTERVAL_MS,
  tokenBundle,
};
export default IdentityVerificationModal;
